import React, { useState, useEffect } from 'react'

const SECTIONS = [
  {
    emoji: '💬',
    titre: 'Formule les défis positivement',
    couleur: '#dbeafe',
    couleurTexte: '#1a5fa8',
    contenu: `Les défis doivent toujours être formulés de façon positive, sans négation. Le cerveau des enfants (et de tous les êtres humains !) ne traite pas bien le "ne pas". Préfère une action concrète à accomplir.`,
    exemples: [
      { mauvais: '❌ Ne pas dire de gros mots', bon: '✅ Utiliser un vocabulaire adapté' },
      { mauvais: '❌ Ne pas taper', bon: '✅ Utiliser des gestes appropriés' },
      { mauvais: '❌ Ne pas manger salement', bon: '✅ Manger proprement à table' },
      { mauvais: '❌ Arrêter de crier', bon: '✅ Parler avec une voix douce' },
    ]
  },
  {
    emoji: '🎯',
    titre: 'Entre 3 et 6 défis maximum',
    couleur: '#dcfce7',
    couleurTexte: '#166534',
    contenu: `Trop de défis à la fois peut décourager l'enfant. Commence avec 3 défis bien choisis, et ajoute-en progressivement quand il les maîtrise. Cible les comportements les plus importants pour la famille au quotidien.`,
    exemples: [
      { bon: '✅ 3 défis pour commencer, puis augmenter progressivement' },
      { bon: '✅ Choisir des défis réalistes et atteignables chaque jour' },
      { bon: '✅ Impliquer l\'enfant dans le choix des défis si possible' },
    ]
  },
  {
    emoji: '⭐',
    titre: 'Les points reflètent l\'importance',
    couleur: '#fef9c3',
    couleurTexte: '#92400e',
    contenu: `Attribue plus de points aux défis les plus importants ou les plus difficiles pour l'enfant. Un défi facile vaut 1 point, un défi clé peut valoir 2 ou 3 points. Ça aide l'enfant à comprendre les priorités.`,
    exemples: [
      { bon: '⭐ Défi facile (ex: ranger ses affaires) → 1 point' },
      { bon: '⭐⭐ Défi important (ex: voix adaptée) → 2 points' },
      { bon: '⭐⭐⭐ Défi clé pour la famille → 3 points' },
    ]
  },
  {
    emoji: '🎁',
    titre: 'Définir un moment pour les récompenses',
    couleur: '#f3e8ff',
    couleurTexte: '#6b21a8',
    contenu: `Fixe un moment régulier et prévisible pour l'échange des points contre les récompenses. La prévisibilité est très importante pour les enfants neurodivergents. L'enfant peut échanger chaque jour ou chaque semaine — mais ce moment doit être fixe.`,
    exemples: [
      { bon: '📅 Chaque soir après le dîner : échange quotidien' },
      { bon: '📅 Chaque dimanche matin : échange hebdomadaire' },
      { bon: '📅 Le vendredi soir : bilan de semaine + récompense' },
    ]
  },
  {
    emoji: '🚫',
    titre: 'Ne jamais enlever de points',
    couleur: '#fee2e2',
    couleurTexte: '#991b1b',
    contenu: `Ce tableau est UNIQUEMENT un tableau de valorisation. On ne retire jamais de points, même en cas de mauvais comportement. Enlever des points détruit la motivation et la confiance. Si un comportement négatif se produit, l'enfant ne gagne simplement pas de points ce jour-là — c'est tout.`,
    exemples: [
      { mauvais: '❌ "Tu as été violent, je t\'enlève 2 points"', bon: '✅ "Tu n\'as pas eu tes points aujourd\'hui, demain c\'est un nouveau jour"' },
      { bon: '✅ Le manque de points est déjà un message fort — pas besoin d\'en enlever' },
    ]
  },
  {
    emoji: '🌟',
    titre: 'Les bonus : pour les attitudes exceptionnelles',
    couleur: '#fff7ed',
    couleurTexte: '#9a3412',
    contenu: `Les bonus sont des points supplémentaires accordés spontanément pour des comportements très positifs qui ne font pas partie des défis habituels. Ils récompensent l'initiative et la générosité.`,
    exemples: [
      { bon: '🌟 A aidé un autre enfant spontanément' },
      { bon: '🌟 A partagé quelque chose de précieux' },
      { bon: '🌟 A su rester calme malgré une forte frustration' },
      { bon: '🌟 A fait preuve d\'une gentillesse exceptionnelle' },
    ]
  },
  {
    emoji: '🕯️',
    titre: 'La cohérence cardiaque : un outil de régulation',
    couleur: '#e0e7ff',
    couleurTexte: '#3730a3',
    contenu: `L'exercice de cohérence cardiaque (5s inspiration / 5s expiration) aide l'enfant à réguler ses émotions et son attention. Propose-le avant les moments difficiles (devoirs, repas, coucher) ou quand l'enfant est agité. Il est récompensé par des étoiles bonus pour encourager la pratique régulière.`,
    exemples: [
      { bon: '🕯️ Avant les devoirs pour se concentrer' },
      { bon: '🕯️ Quand l\'enfant est en colère ou agité' },
      { bon: '🕯️ Le matin pour bien démarrer la journée' },
    ]
  },
]

export default function Guide({ onFermer }) {
  const [section, setSection] = useState(0)

  return (
    <div style={{
      background: '#fff', borderRadius: 20, maxWidth: 580, width: '100%',
      maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
      boxShadow: '0 25px 60px rgba(0,0,0,0.2)'
    }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1a2e5a, #2d7dd2)', padding: '20px 24px', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, margin: 0, fontWeight: 900 }}>
              📖 Guide KudoGenius
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.8 }}>
              Comment bien utiliser le tableau de valorisation
            </p>
          </div>
          {onFermer && (
            <button onClick={onFermer} style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10,
              padding: '6px 12px', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700,
              fontFamily: "'Nunito', sans-serif"
            }}>✕ Fermer</button>
          )}
        </div>

        {/* Navigation sections */}
        <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
          {SECTIONS.map((s, i) => (
            <button key={i} onClick={() => setSection(i)} style={{
              padding: '4px 10px', borderRadius: 20, border: 'none', fontSize: 11,
              background: section === i ? '#fff' : 'rgba(255,255,255,0.15)',
              color: section === i ? '#1a2e5a' : '#fff',
              cursor: 'pointer', fontWeight: section === i ? 800 : 600,
              fontFamily: "'Nunito', sans-serif"
            }}>
              {s.emoji} {i + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
        {SECTIONS.map((s, i) => i === section && (
          <div key={i}>
            <div style={{
              background: s.couleur, borderRadius: 16, padding: '20px',
              marginBottom: 20, border: `1px solid ${s.couleur}`
            }}>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: s.couleurTexte, marginBottom: 12, fontWeight: 900 }}>
                {s.emoji} {s.titre}
              </h3>
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.7, margin: 0 }}>
                {s.contenu}
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {s.exemples.map((ex, j) => (
                <div key={j}>
                  {ex.mauvais && (
                    <div style={{
                      background: '#fef2f2', borderRadius: 10, padding: '10px 14px',
                      fontSize: 13, color: '#991b1b', marginBottom: 6,
                      border: '1px solid #fecaca'
                    }}>
                      {ex.mauvais}
                    </div>
                  )}
                  <div style={{
                    background: '#f0fdf4', borderRadius: 10, padding: '10px 14px',
                    fontSize: 13, color: '#166534', fontWeight: ex.mauvais ? 700 : 600,
                    border: '1px solid #bbf7d0'
                  }}>
                    {ex.bon}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Navigation bas */}
      <div style={{
        padding: '16px 24px', borderTop: '1px solid #e5e7eb',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#f8faff'
      }}>
        <button
          onClick={() => setSection(s => Math.max(0, s - 1))}
          disabled={section === 0}
          style={{
            padding: '8px 18px', borderRadius: 10, border: 'none',
            background: section === 0 ? '#e5e7eb' : '#1a2e5a',
            color: section === 0 ? '#9ca3af' : '#fff',
            cursor: section === 0 ? 'not-allowed' : 'pointer',
            fontWeight: 700, fontSize: 13, fontFamily: "'Nunito', sans-serif"
          }}
        >← Précédent</button>

        <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 600 }}>
          {section + 1} / {SECTIONS.length}
        </span>

        {section < SECTIONS.length - 1 ? (
          <button
            onClick={() => setSection(s => s + 1)}
            style={{
              padding: '8px 18px', borderRadius: 10, border: 'none',
              background: '#2d7dd2', color: '#fff',
              cursor: 'pointer', fontWeight: 700, fontSize: 13,
              fontFamily: "'Nunito', sans-serif"
            }}
          >Suivant →</button>
        ) : (
          <button
            onClick={onFermer}
            style={{
              padding: '8px 18px', borderRadius: 10, border: 'none',
              background: '#22c55e', color: '#fff',
              cursor: 'pointer', fontWeight: 700, fontSize: 13,
              fontFamily: "'Nunito', sans-serif"
            }}
          >✓ Compris !</button>
        )}
      </div>
    </div>
  )
}
